'use client';
/**
 * LE RÉCAPITULATIF COMPTABLE (RQ, 30/09/2026) — le tableau remis chaque mois au
 * cabinet. Depuis le 02/11/2026, tout est tiré du PLANNING (présences, nuits,
 * dimanches, fériés) et des absences validées ; seuls les retards viennent
 * encore du pointage. Variable = coefficient × (dimanches + fériés) : les
 * nuits n'y entrent pas. Le coefficient et le matricule de paie se saisissent
 * sur la fiche du personnel. Règles : `backend/src/rh-recap-comptable.ts`.
 */
type LigneRecap = {
  orgId: string; matricule: string | null; matriculePaie: string | null; nom: string;
  presence: number; absence: number; conges: number; retards: number;
  nuits: number; dimanches: number; feries: number; gardesPlanning: number;
  joursNonMesures: number; joursAValider: number; nuitsNonBadgees: string[];
  detail: { dimanches: string[]; feries: string[]; nuits: string[] };
  coefficient: number | null; variable: number | null; variableTexte: string; variableFormule: string; nouveau: boolean;
};
function RecapComptable({ mois }: { mois: string }) {
  const [d, setD] = React.useState<{ lignes: LigneRecap[]; notes: string[]; provisoire: boolean; calendrier?: { ouvres: number; feries: number; dimanches: number } } | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);
  const charger = React.useCallback(async (format = '') => {
    const r = await fetch(`/api/ged/ged/rh/paie/recap-comptable?mois=${mois}${format ? `&format=${format}` : ''}`, { credentials: 'include' });
    if (!r.ok) throw new Error((await r.json().catch(() => null))?.error?.message || 'Récapitulatif illisible — réessayez.');
    return r.json();
  }, [mois]);
  React.useEffect(() => {
    let vivant = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- efface le mois précédent pendant la requête
    setD(null); setErreur('');
    charger().then((x) => { if (vivant) setD(x); }).catch((e: Error) => { if (vivant) setErreur(e.message); });
    return () => { vivant = false; };
  }, [charger]);
  const telecharger = async () => {
    setEnvoi(true);
    try {
      const x = await charger('xlsx');
      const bin = Uint8Array.from(atob(x.contenuBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bin], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = x.fichier; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setErreur((e as Error).message); } finally { setEnvoi(false); }
  };
  const jj = (l: string[]) => l.map((x) => x.slice(8, 10)).join(', ');
  const nbr = (v: number) => (v ? v : '—');
  const petit: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' };
  return (
    <div style={{ ...card, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, margin: 0 }}>Récapitulatif comptable — {nomMois(mois)}</h2>
        {d?.provisoire && <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>PROVISOIRE</span>}
        {/* Le calendrier du mois (RQ, 01/10/2026). */}
        {d?.calendrier && (
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            <strong>{d.calendrier.ouvres}</strong> jours ouvrés · <strong>{d.calendrier.feries}</strong> férié{d.calendrier.feries > 1 ? 's' : ''} · <strong>{d.calendrier.dimanches}</strong> dimanches
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" type="button" disabled={!d || envoi} onClick={() => void telecharger()}>
          <I n="download" s={13} /> {envoi ? 'Préparation…' : 'Télécharger (Excel)'}
        </Bouton>
      </div>
      {erreur ? <p style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</p> : !d ? <Skeleton rows={6} /> : (
        <>
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            <Tableau
              id="rh-recap-comptable"
              /* L5 (10/10/2026) : niveau STANDARD — filtres, masquage. Pas d'export
                 du socle : le classeur Excel du serveur est l'export de référence. */
              niveau="standard"
              vide={{ titre: 'Aucune ligne pour ce mois.' }}
              lignes={d.lignes}
              cleLigne={(l) => l.orgId}
              /* PLUS DE DÉFILEMENT À DROITE (RQ, 01/10/2026) : les colonnes
                 se partagent la largeur de l'écran et les en-têtes passent à
                 la ligne. */
              ajusterLargeur="fixe"
              colonnes={[
                /* Le numéro de BADGEUSE, préfixé « B » (RQ, 30/09/2026) : sans quoi il se
                   confondait avec le matricule de paie, la colonne d'à côté. */
                { cle: 'matricule', libelle: 'Badge', type: 'nombre', valeur: (l) => Number(l.matricule) || null, rendu: (l) => (l.matricule ? `B${l.matricule}` : '—') },
                {
                  cle: 'paie', libelle: 'Mat. paie', type: 'nombre', valeur: (l) => Number(l.matriculePaie) || null,
                  rendu: (l) => l.matriculePaie || <span style={{ color: 'var(--amber-600)' }}>à saisir</span>,
                },
                {
                  cle: 'nom', libelle: 'Salarié', valeur: (l) => l.nom, epinglee: true, obligatoire: true, style: { textAlign: 'start', width: '17%' },
                  rendu: (l) => (
                    <>
                      <strong style={{ fontWeight: 600 }}>{l.nom}</strong>
                      {l.nouveau && <span style={{ marginInlineStart: 6, fontSize: 10.5, fontWeight: 700, color: 'var(--amber-600)' }}>NOUVEAU</span>}
                      {(l.joursAValider > 0 || l.joursNonMesures > 0) && (
                        <span style={petit}>{[l.joursAValider ? `${l.joursAValider} j à valider` : '', l.joursNonMesures ? `${l.joursNonMesures} j non mesurés` : ''].filter(Boolean).join(' · ')}</span>
                      )}
                    </>
                  ),
                },
                { cle: 'presence', type: 'nombre', libelle: 'Présence', valeur: (l) => l.presence, style: { textAlign: 'end' }, rendu: (l) => (l.joursNonMesures && !l.presence ? '—' : nbr(l.presence)) },
                { cle: 'absence', type: 'nombre', libelle: 'Absence', valeur: (l) => l.absence, style: { textAlign: 'end' }, rendu: (l) => nbr(l.absence) },
                { cle: 'conges', type: 'nombre', libelle: 'Congés', valeur: (l) => l.conges, style: { textAlign: 'end' }, rendu: (l) => nbr(l.conges) },
                { cle: 'retards', type: 'nombre', libelle: 'Retards', valeur: (l) => l.retards, style: { textAlign: 'end' }, rendu: (l) => nbr(l.retards) },
                {
                  cle: 'nuits', libelle: 'Nuits', type: 'nombre', valeur: (l) => l.nuits, style: { textAlign: 'end' },
                  rendu: (l) => (
                    <span title={l.nuits ? `Nuits : ${jj(l.detail.nuits)}` : undefined}>
                      {nbr(l.nuits)}
                      {l.nuitsNonBadgees.length > 0 && (
                        <span title="Nuits planifiées sans aucun passage lu : non comptées. Régularisez dans Présences si la garde a eu lieu."
                          style={{ ...petit, color: 'var(--red-600)' }}>+ {l.nuitsNonBadgees.length} sans badge</span>
                      )}
                    </span>
                  ),
                },
                { cle: 'dim', libelle: 'Dimanches', type: 'nombre', valeur: (l) => l.dimanches, style: { textAlign: 'end' }, rendu: (l) => <span title={l.dimanches ? `Dimanches : ${jj(l.detail.dimanches)}` : undefined}>{nbr(l.dimanches)}</span> },
                { cle: 'fer', libelle: 'Fériés', type: 'nombre', valeur: (l) => l.feries, style: { textAlign: 'end' }, rendu: (l) => <span title={l.feries ? `Fériés : ${jj(l.detail.feries)}` : undefined}>{nbr(l.feries)}</span> },
                {
                  cle: 'coef', libelle: 'Coef.', type: 'nombre', valeur: (l) => l.coefficient, style: { textAlign: 'end' },
                  rendu: (l) => (l.coefficient != null ? String(l.coefficient).replace('.', ',')
                    : (l.dimanches + l.feries) ? <span style={{ color: 'var(--amber-600)' }}>à saisir</span> : '—'),
                },
                {
                  cle: 'variable', libelle: 'Variable', type: 'nombre', valeur: (l) => l.variable, style: { textAlign: 'end' },
                  rendu: (l) => (l.variable != null ? (
                    <>
                      <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{String(l.variable).replace('.', ',')}</strong>
                      <span style={petit}>{l.variableFormule.replace(/\./g, ',')}</span>
                    </>
                  ) : l.variableTexte ? <span style={{ color: 'var(--amber-600)', fontSize: 12 }}>{l.variableTexte}</span> : '—'),
                },
              ]}
            />
          </div>
          <ul style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '10px 0 0', paddingInlineStart: 18 }}>
            {d.notes.map((x) => <li key={x}>{x}</li>)}
            <li>Survolez une nuit, un dimanche ou un férié pour voir les dates. Le coefficient et le matricule de paie se saisissent sur la fiche du personnel.</li>
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Paie (lot 4) — la finalité du chantier.
 *
 * ⚠️ Cet écran ne montre AUCUN montant, et n'en montrera jamais (D1). Il produit
 * des quantités que le gestionnaire recopie dans son logiciel de paie. Le calcul
 * de paie tunisien change à chaque loi de finances : l'embarquer ferait porter
 * au laboratoire un risque réglementaire pour un gain nul.
 *
 * L'écran est construit autour d'une idée : **on ne clôture pas à l'aveugle**.
 * Les contrôles de cohérence sont affichés AVANT le bouton, pas après — parce
 * qu'un mois clos ne se rouvre pas, il se régularise.
 */
import React from 'react';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import type { RhVariablesPaie } from '@/lib/types';
import { I, TitrePage, card, inputStyle, EmptyState, ConfirmButton, useEscape, Help, FilterBar, Skeleton, Selecteur } from '@/components/ui';
import { AccesRefuseRh, Modale, lbl } from '@/components/screens/RhAbsences';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';
import { nomDe } from '@/lib/nom';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { fmtDate } from '@/lib/meta';
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const nomMois = (p: string) => { const [a, m] = p.split('-'); return `${MOIS[Number(m) - 1]} ${a}`; };
const n = (v: number) => (v ? String(Math.round(v * 100) / 100) : '—');

export function Paie() {
  const { session, rhAcces, rhPaie, rhPeriode, setRhPeriode, loadPaie, cloturerPaie, exporterPaie, regulariserPaie, rhPersonnel, rhChargement } = useGed();
  const [regul, setRegul] = React.useState(false);
  const [q, setQ] = React.useState('');
  const estRQ = session.roles.includes('rq');
  const authForcage = useAutorisationResponsable();
  const [forcageOuvert, setForcageOuvert] = React.useState(false);
  React.useEffect(() => { void loadPaie(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (!rhAcces?.acces) return <AccesRefuseRh />;
  // `rhPaie` reste `null` dans DEUX cas : la lecture est en cours, ou elle a
  // échoué (le magasin avale l'erreur). Afficher « Chargement… » dans le second
  // laisserait un écran qui attend pour toujours quelque chose qui ne viendra pas.
  if (!rhPaie) {
    return rhChargement > 0
      ? <div style={{ ...card, padding: 0, overflow: 'hidden', marginTop: 14 }}><Skeleton rows={6} /></div>
      : (
        <EmptyState
          titre="Les variables du mois n’ont pas pu être lues"
          indice="La lecture a échoué ou n’a rien renvoyé. Réessayez ; si cela persiste, le service est probablement indisponible."
          action={<Bouton onClick={() => void loadPaie()}>Réessayer</Bouton>} />
      );
  }

  const c = rhPaie.controles;
  const close = rhPaie.statut === 'close';
  const tousActifs = rhPaie.lignes.filter((l) => l.joursTravailles > 0 || Object.keys(l.absences || {}).length > 0);
  // Le tableau fait une ligne par salarié et une vingtaine de colonnes : vérifier
  // une personne avant de clôturer suppose de la trouver, pas de la balayer.
  const qq = q.trim().toLowerCase();
  const actifs = qq
    ? tousActifs.filter((l) => `${l.nom} ${l.matriculePaie || ''} ${l.matricule || ''}`.toLowerCase().includes(qq))
    : tousActifs;
  const bloquants = (c?.journeesNonValidees ?? 0) > 0;
  const avertissements = [
    ...(c?.salariesSansContrat?.length ? [`${c.salariesSansContrat.length} salarié(s) sans contrat : leurs heures supplémentaires ne sont pas calculées`] : []),
    ...(c?.salariesSansMatricule?.length ? [`${c.salariesSansMatricule.length} salarié(s) sans matricule de paie : leur ligne sortira sans clé comptable — le rapprochement sera manuel`] : []),
    ...(c?.demandesEnAttente ? [`${c.demandesEnAttente} demande(s) de congé encore en attente sur la période`] : []),
  ];

  const decaler = (d: number) => {
    const [a, m] = rhPeriode.split('-').map(Number);
    const dt = new Date(a, m - 1 + d, 1);
    setRhPeriode(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Préparation de la paie"
        sousTitre={<>
          Les <strong>quantités</strong> à reprendre dans votre logiciel de paie : jours travaillés,
          heures, majorations, absences par motif. Aucun montant n’est calculé ici — le barème et les
          cotisations restent l’affaire de votre logiciel de paie et de votre expert-comptable.
        </>} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={() => decaler(-1)}><I n="chevron-left" s={14} /> Mois précédent</Bouton>
        <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 15, minWidth: 150, textAlign: 'center' }}>{nomMois(rhPeriode)}</strong>
        <Bouton taille="sm" onClick={() => decaler(1)}>Mois suivant <I n="chevron-right" s={14} /></Bouton>
        <span style={{
          fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '4px 11px',
          background: close ? 'var(--green-50)' : 'var(--gray-25)', color: close ? 'var(--green-600)' : 'var(--text-muted)',
        }}>
          {close ? 'CLÔTURÉ' : 'OUVERT'}
        </span>
        {close && rhPaie.empreinte && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-faint)' }}>
            empreinte {rhPaie.empreinte} · par {rhPaie.closePar}
          </span>
        )}
      </div>

      <RecapComptable mois={rhPeriode} />

      {close ? (
        <div style={{ ...card, padding: 20, background: 'var(--green-50)', border: '1px solid var(--green-600)' }}>
          <strong style={{ fontSize: 13, color: 'var(--green-600)' }}>Mois arrêté</strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0' }}>
            Les variables sont figées : deux exports de ce mois sont identiques. Une correction
            découverte maintenant ne réécrit pas l’historique — elle crée une <strong>régularisation
            datée</strong>, transmise avec le mois suivant.
          </p>
        </div>
      ) : (
        <>
          {bloquants && (
            <div style={{ ...card, padding: 20, background: 'var(--red-50)', border: '1px solid var(--red-500)' }}>
              <strong style={{ fontSize: 13, color: 'var(--red-600)' }}>
                {c!.journeesNonValidees} journée(s) de présence non validée(s)
              </strong>
              <p style={{ fontSize: 12.5, margin: '4px 0 0' }}>
                {c!.detailNonValidees.join(' · ')}{c!.journeesNonValidees > 10 ? ' …' : ''}
              </p>
              <p style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                Les faire entrer en paie reviendrait à payer sur la seule foi de la badgeuse.
                Validez-les dans <strong>Présences</strong>.
              </p>
            </div>
          )}
          {avertissements.length > 0 && (
            <div style={{ ...card, padding: 20, background: 'var(--amber-50)', border: '1px solid var(--amber-500)', marginTop: bloquants ? 10 : 0 }}>
              <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>À vérifier avant de clôturer</strong>
              <ul style={{ fontSize: 12.5, margin: '4px 0 0', paddingInlineStart: 18 }}>
                {avertissements.map((a) => <li key={a}>{a}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {tousActifs.length > 6 && (
        <div style={{ marginTop: 14 }}>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un salarié, un matricule…"
            total={tousActifs.length} affiches={actifs.length}
            onReset={() => setQ('')} />
        </div>
      )}

      {!actifs.length ? (
        <EmptyState
          titre={tousActifs.length === 0 ? 'Aucune variable pour ce mois' : 'Aucun salarié ne correspond'}
          indice={tousActifs.length === 0
            ? 'Aucune journée validée sur la période. Importez les pointages et validez les présences.'
            : `${tousActifs.length} salarié(s) ont des variables ce mois-ci — seule la recherche les masque.`} />
      ) : (
        /* ⚠️ `defilementHorizontal` et non un `overflowX` posé sur la carte
           (relevé du 27/08/2026, P7) : le tableau dépassait de 33 px — 1 187
           dans 1 154 — et l'ascenseur horizontal était celui de la CARTE,
           invisible tant qu'on ne survolait pas le bas. Porté par le composant,
           il est déclaré, et l'en-tête reste collant à la boîte, ce qui est le
           bon comportement pour un tableau dont on a borné la hauteur.
           `hauteurMax` est CONSERVÉ ici, contrairement au reste de
           l'application : c'est un tableau de saisie mensuelle qu'on relit
           ligne à ligne, et son en-tête de colonnes est le seul moyen de savoir
           quelle variable on remplit. */
        <div style={{ ...card, padding: 0, marginTop: 14 }}>
          <Tableau
            id="rh-paie"
            /* L5 (10/10/2026) : niveau AVANCÉ, « Salarié » ÉPINGLÉ (vingt colonnes :
               on lit le nom pendant qu'on défile), filtres, masquage. Pas d'export
               du socle : « Exporter pour la paie (CSV) » est généré par le
               serveur, avec son empreinte — c'est lui l'export de référence. */
            niveau="avance"
            vide={{ titre: 'Aucune variable pour ce mois.' }}
            lignes={actifs}
            cleLigne={(l) => String(l.orgId)}
            hauteurMax={600}
            defilementHorizontal
            colonnes={[
              {
                cle: 'salarie', libelle: 'Salarié', epinglee: true, obligatoire: true,
                valeur: (l) => l.nom,
                rendu: (l) => (
                  <>
                    <span style={{ fontWeight: 600 }}>{l.nom}</span>
                    {/* ⚠️ Le matricule de PAIE : c'est lui que le comptable
                        recopie. Le numéro de badgeuse ne lui sert à rien. */}
                    <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, color: l.matriculePaie ? 'var(--text-faint)' : 'var(--amber-600)' }}>
                      {l.matriculePaie || 'sans matricule de paie'}
                    </span>
                    {/* Une RÉSERVE est ce qui empêche de reporter la ligne en
                        paie telle quelle : elle reste attachée au nom, jamais
                        reléguée dans une colonne qu'on pourrait déplacer au
                        bout du tableau. */}
                    {l.reserves.map((r) => (
                      <span key={r} style={{ display: 'block', fontSize: 12.5, color: 'var(--amber-600)' }}>⚠ {r}</span>
                    ))}
                  </>
                ),
                style: { textAlign: 'start' },
              },
              { cle: 'jours', libelle: 'Jours', type: 'nombre', valeur: (l) => l.joursTravailles, rendu: (l) => n(l.joursTravailles) },
              { cle: 'heures', libelle: 'Heures', type: 'nombre', valeur: (l) => l.heuresTotal, rendu: (l) => n(l.heuresTotal), style: { fontWeight: 700 } },
              {
                cle: 'sup', libelle: 'dont sup.', type: 'nombre', valeur: (l) => l.heuresSup,
                /* Les heures supplémentaires se colorent : c'est la ligne qu'un
                   gestionnaire de paie cherche en premier, et trier dessus doit
                   la faire ressortir aussi bien qu'avant. */
                rendu: (l) => (
                  <span style={{ color: l.heuresSup ? 'var(--amber-600)' : undefined, fontWeight: l.heuresSup ? 700 : 400 }}>
                    {n(l.heuresSup)}
                  </span>
                ),
              },
              { cle: 'nuit', libelle: 'Nuit', type: 'nombre', valeur: (l) => l.heuresNuit, rendu: (l) => n(l.heuresNuit) },
              { cle: 'dim', libelle: 'Dimanche', type: 'nombre', valeur: (l) => l.heuresDimanche, rendu: (l) => n(l.heuresDimanche) },
              { cle: 'ferie', libelle: 'Fériés', type: 'nombre', valeur: (l) => l.heuresFerie, rendu: (l) => n(l.heuresFerie) },
              { cle: 'gardes', libelle: 'Gardes', type: 'nombre', valeur: (l) => l.nbGardes, rendu: (l) => n(l.nbGardes) },
              ...rhPaie.motifs.map((m) => ({
                cle: `abs-${m.code}`, libelle: m.label, type: 'nombre' as const,
                valeur: (l: RhVariablesPaie) => l.absences?.[m.code] || 0,
                rendu: (l: RhVariablesPaie) => n(l.absences?.[m.code] || 0),
              })),
            ]}
          />
        </div>
      )}

      {(rhPaie.regularisationsRecues?.length ?? 0) > 0 && (
        <Bloc titre={`Régularisations reçues d’un mois antérieur (${rhPaie.regularisationsRecues!.length})`}>
          {rhPaie.regularisationsRecues!.map((r) => (
            <p key={r.id} style={{ fontSize: 12.5, margin: '3px 0' }}>
              <strong>{r.nom}</strong> : {r.quantite > 0 ? '+' : ''}{r.quantite} {r.unite} — {r.libelle}
              <span style={{ color: 'var(--text-faint)' }}> (par {r.auteurNom})</span>
            </p>
          ))}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>
            À reporter dans le logiciel de paie en plus des variables du mois.
          </p>
        </Bloc>
      )}

      {(rhPaie.regularisations?.length ?? 0) > 0 && (
        <Bloc titre="Régularisations posées sur ce mois clos">
          {rhPaie.regularisations!.map((r) => (
            <p key={r.id} style={{ fontSize: 12.5, margin: '3px 0' }}>
              <strong>{r.nom}</strong> : {r.quantite > 0 ? '+' : ''}{r.quantite} {r.unite} — {r.libelle}
              <span style={{ color: 'var(--text-faint)' }}> → transmise avec {r.reporteeSur}</span>
            </p>
          ))}
        </Bloc>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <Bouton onClick={() => exporterPaie(rhPeriode)}>
          <I n="doc" s={14} /> Exporter pour la paie (CSV)
        </Bouton>
        {!close && (!bloquants || estRQ) && (
          <ConfirmButton
            label={`Clôturer ${nomMois(rhPeriode)}`} variant="primary" icon="check"
            question={bloquants
              ? `${c!.journeesNonValidees} journée(s) ne sont pas validées. Clôturer quand même ?`
              : `Clôturer ${nomMois(rhPeriode)} ? Les variables seront figées : toute correction ultérieure passera par une régularisation.`}
            confirmer="Clôturer"
            onConfirm={() => cloturerPaie(rhPeriode, bloquants)}
          />
        )}
        {!close && bloquants && !estRQ && !forcageOuvert && (
          <Bouton variante="primaire" onClick={() => setForcageOuvert(true)}>
            <I n="check" s={14} /> Clôturer {nomMois(rhPeriode)}
          </Bouton>
        )}
        {close && (
          <Bouton onClick={() => setRegul(true)}>
            <I n="plus" s={14} /> Ajouter une régularisation
          </Bouton>
        )}
      </div>

      {!close && bloquants && !estRQ && forcageOuvert && (
        <div style={{ ...card, padding: 12, marginTop: 10, display: 'grid', gap: 8, borderInlineStart: '3px solid var(--amber-500)' }}>
          <strong>{c!.journeesNonValidees} journée(s) ne sont pas validées. Clôturer quand même ?</strong>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
            Forcer la clôture de paie malgré des journées non validées relève du RQ.
          </p>
          <ChampsResponsable {...authForcage} libelle="cette clôture" />
          <PiedActions taille="sm" annuler={{ onClick: () => { setForcageOuvert(false); authForcage.reinitialiser(); } }} principale={{ libelle: "Clôturer", disabled: !authForcage.pret, onClick: async () => {
                const ok = await cloturerPaie(rhPeriode, true, authForcage.payload);
                if (ok) { setForcageOuvert(false); authForcage.reinitialiser(); }
              } }} />
        </div>
      )}

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 10, maxWidth: 800 }}>
        L’export est un fichier CSV (séparateur « ; », encodage UTF-8) qu’Excel et LibreOffice
        ouvrent directement. Sur un mois clôturé, il est reproductible à l’identique — c’est ce que
        garantit l’empreinte.
      </p>

      <HistoriquePeriodes courante={rhPeriode} onAller={setRhPeriode} />

      {regul && (
        <FormulaireRegularisation
          periode={rhPeriode}
          personnel={rhPersonnel.filter((p) => p.type === 'salarie')}
          onFermer={() => setRegul(false)}
          onValider={async (r) => { const ok = await regulariserPaie(r); if (ok) setRegul(false); }}
        />
      )}
    </section>
  );
}

/* ─────────── L'historique des périodes de paie (GED-15 c) ──────────────────
 *
 * `GET /ged/rh/paie/periodes` était autorisée depuis sa livraison sans qu'aucun
 * écran ne l'atteigne. Cet écran ne connaissait qu'UN mois à la fois, celui
 * qu'on atteint en cliquant sur une flèche : rien ne disait quels mois avaient
 * été arrêtés, par qui, ni sous quelle empreinte — alors que c'est exactement
 * la question posée en audit, et celle que se pose le gestionnaire qui reprend
 * le dossier de quelqu'un d'autre.
 *
 * Deux usages, et c'est pourquoi le panneau est aussi une NAVIGATION :
 *  · le contrôle — un mois clos porte une empreinte, et deux exports du même
 *    mois clos doivent être identiques. L'empreinte est donc montrée telle
 *    qu'elle est stockée, sans reformatage ;
 *  · l'accès — atteindre septembre depuis février demandait sept clics sur la
 *    flèche. Ici on clique sur le mois.
 *
 * ⚠️ Le panneau ne montre PAS le contenu des périodes, seulement leur état.
 * Les variables d'un mois se lisent en l'ouvrant, par le chemin normal, avec
 * les contrôles de cohérence qui vont avec.
 */
type PeriodePaie = {
  periode: string; statut: string; closePar: string | null; closeLe: string | null;
  empreinte: string | null;
  regularisations: { libelle?: string; quantite?: number; unite?: string; personnelOrgId?: string }[];
};

function HistoriquePeriodes({ courante, onAller }: {
  courante: string; onAller: (p: string) => void;
}) {
  const [ouvert, setOuvert] = React.useState(false);
  const [periodes, setPeriodes] = React.useState<PeriodePaie[] | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [lecture, setLecture] = React.useState(false);

  const lire = () => {
    setLecture(true);
    void fetch('/api/ged/ged/rh/paie/periodes', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        // Un échec est DIT : « aucune période clôturée » et « la lecture a
        // échoué » ne se ressemblent pas, et le premier serait un contresens
        // sur un laboratoire qui clôture tous les mois.
        if (j) { setPeriodes(j.periodes || []); setErreur(''); } else setErreur('L’historique des périodes n’a pas pu être lu.');
      })
      .catch(() => setErreur('L’historique des périodes n’a pas pu être lu.'))
      .finally(() => setLecture(false));
  };
  const basculer = () => {
    const versOuvert = !ouvert;
    setOuvert(versOuvert);
    if (!versOuvert || periodes || lecture) return;
    lire();
  };

  return (
    <div style={{ ...card, padding: 0, marginTop: 18, overflow: 'hidden' }}>
      <button onClick={basculer} aria-expanded={ouvert} className="ged-btn"
        style={{ width: '100%', textAlign: 'start', background: 'transparent', border: 'none',
          cursor: 'pointer', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={14} />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Historique des périodes de paie
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          quels mois sont arrêtés, par qui, sous quelle empreinte
        </span>
        <span style={{ flex: 1 }} />
        {lecture && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>lecture…</span>}
      </button>

      {ouvert && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-default)' }}>
          {/* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle — la ligne
              ouvre le mois ; états intégrés : lecture, échec DIT (« aucune période
              clôturée » et « la lecture a échoué » ne se ressemblent pas), vide. */}
          <div style={{ marginTop: 12 }}>
            <Tableau
              id="rh-paie-periodes"
              niveau="simple"
              chargement={lecture && !periodes}
              erreur={erreur || null}
              onReessayer={() => { setErreur(''); lire(); }}
              vide={{
                titre: 'Aucune période n’a encore été arrêtée.',
                indice: 'Un mois n’apparaît ici qu’une fois clôturé — c’est la clôture qui fige les variables et calcule l’empreinte.',
              }}
              lignes={periodes || []}
              cleLigne={(p) => p.periode}
              ligneProps={(p) => ligneOuvrable(() => onAller(p.periode), `Ouvrir ${nomMois(p.periode)}`,
                { background: p.periode === courante ? 'var(--gray-25)' : undefined })}
              colonnes={[
                { cle: 'periode', libelle: 'Mois', valeur: (p) => p.periode, rendu: (p) => <strong>{nomMois(p.periode)}</strong> },
                {
                  cle: 'statut', libelle: 'État', type: 'liste', valeur: (p) => p.statut,
                  libelleValeur: (v) => (v === 'close' ? 'Clôturé' : 'Ouvert'),
                  rendu: (p) => {
                    const close = p.statut === 'close';
                    return (
                      <span style={{
                        fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
                        background: close ? 'var(--green-50)' : 'var(--gray-25)',
                        color: close ? 'var(--green-600)' : 'var(--text-muted)',
                      }}>{close ? 'CLÔTURÉ' : 'OUVERT'}</span>
                    );
                  },
                },
                {
                  cle: 'closeLe', libelle: 'Arrêté', type: 'date', valeur: (p) => p.closeLe || '', style: { color: 'var(--text-muted)' },
                  rendu: (p) => (p.closeLe ? `${fmtDate(p.closeLe)}${p.closePar ? ` · ${p.closePar}` : ''}` : '—'),
                },
                { cle: 'empreinte', libelle: 'Empreinte', valeur: (p) => p.empreinte || '', style: { fontFamily: 'var(--font-mono)', color: 'var(--text-faint)' }, rendu: (p) => p.empreinte || '—' },
                { cle: 'regularisations', libelle: 'Régularisations', type: 'nombre', valeur: (p) => p.regularisations.length, rendu: (p) => p.regularisations.length || '—' },
              ]}
            />
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.55, maxWidth: '84ch' }}>
            L’<strong>empreinte</strong> est ce qui rend un mois clos reproductible : deux exports du
            même mois doivent la porter à l’identique. Une correction découverte après la clôture ne
            réécrit jamais ces lignes — elle crée une régularisation datée, transmise avec le mois
            suivant. Cliquer sur un mois l’ouvre ; le détail des variables ne se lit que là, avec ses
            contrôles de cohérence.
          </p>
        </div>
      )}
    </div>
  );
}

function FormulaireRegularisation({ periode, personnel, onFermer, onValider }: {
  periode: string;
  personnel: { orgId: string; nom: string }[];
  onFermer: () => void;
  onValider: (r: { periode: string; personnelOrgId: string; libelle: string; quantite: number; unite: string }) => Promise<void>;
}) {
  const [qui, setQui] = React.useState(personnel[0]?.orgId || '');
  const [quantite, setQuantite] = React.useState('0');
  const [unite, setUnite] = React.useState('heures');
  const [libelle, setLibelle] = React.useState('');
  // Sans ce verrou, un double-clic crée DEUX régularisations : la seconde est
  // aussi valide que la première aux yeux du serveur, et elle part en paie.
  const [envoi, setEnvoi] = React.useState(false);
  useEscape(true, onFermer);
  const incomplet = libelle.trim().length < 5 || !qui;

  return (
    <Modale titre={`Régularisation sur ${nomMois(periode)}`} onFermer={onFermer}>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {nomMois(periode)} est clôturé et ne sera pas modifié. Cette régularisation sera
        <strong> transmise avec le mois suivant</strong>, à votre nom et avec sa date.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginTop: 12 }}>
        <label style={lbl}>Salarié
          <Selecteur style={{ ...inputStyle, width: '100%' }} value={qui} onChange={setQui}>
            {personnel.map((p) => <option key={p.orgId} value={p.orgId}>{nomDe(p)}</option>)}
          </Selecteur>
        </label>
        <label style={lbl}>Quantité
          <Help aide="Négative pour retirer, positive pour ajouter.">signe</Help>
          <input type="number" step="0.25" style={{ ...inputStyle, width: '100%' }} value={quantite} onChange={(e) => setQuantite(e.target.value)} />
        </label>
        <label style={lbl}>Unité
          <select style={{ ...inputStyle, width: '100%' }} value={unite} onChange={(e) => setUnite(e.target.value)}>
            <option value="heures">heures</option>
            <option value="jours">jours</option>
          </select>
        </label>
      </div>
      <label style={{ ...lbl, marginTop: 10 }}>Motif
        <textarea rows={2} style={{ ...inputStyle, width: '100%' }} value={libelle} onChange={(e) => setLibelle(e.target.value)}
          placeholder="Ex. : 6 h retirées — erreur de pointage constatée après clôture." />
      </label>
      <PiedActions taille="sm" style={{ marginTop: 16 }} annuler={{ onClick: onFermer, disabled: envoi }} principale={{ libelle: 'Enregistrer la régularisation', libelleChargement: 'Enregistrement…', chargement: envoi, disabled: incomplet || envoi, onClick: async () => {
            setEnvoi(true);
            try { await onValider({ periode, personnelOrgId: qui, libelle: libelle.trim(), quantite: Number(quantite), unite }); }
            finally { setEnvoi(false); }
          } }} />
    </Modale>
  );
}

function Bloc({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 20, marginTop: 14 }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, margin: '0 0 8px' }}>{titre}</h2>
      {children}
    </div>
  );
}

const th: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--tableau-entete-police)', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.03em', color: 'var(--text-muted)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)',
  borderBottom: '1px solid var(--border-default)', background: 'var(--gray-25)', textAlign: 'end',
};
const td: React.CSSProperties = {
  padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', borderBottom: '1px solid var(--border-subtle)',
  fontFamily: 'var(--font-sans)', fontSize: 'var(--tableau-ligne-police)', textAlign: 'end',
};
